// we only plan to support the `ltiResourceLink` type, otherwise this would

import { z } from "zod";

const oidcTokenSchema = z.object({
  iss: z.string(),
  aud: z.union([z.string(), z.array(z.string())]),
  // A missing sub is technically in spec, and implies the user is trying to
  // do an "anonymous" launch, which is not something we plan to support so I'm
  // not allowing it here
  sub: z.string().optional(),
  exp: z.number(),
  iat: z.number(),
  nonce: z.string(),
  azp: z.string().optional(),
});

// See: https://www.imsglobal.org/spec/lti/v1p3#required-message-claims
export const lti1p3IdTokenSchema = oidcTokenSchema.extend({
  "https://purl.imsglobal.org/spec/lti/claim/message_type": z.union([
    z.literal("LtiResourceLinkRequest"),
    z.literal("LtiDeepLinkingRequest"),
  ]),
  "https://purl.imsglobal.org/spec/lti/claim/version": z.literal("1.3.0"),
  "https://purl.imsglobal.org/spec/lti/claim/deployment_id": z.string(),
  "https://purl.imsglobal.org/spec/lti/claim/target_link_uri": z.string(),

  // This is REQUIRED on the LtiResourceLinkRequest message type
  // but OPTIONAL on LtiDeepLinkingRequest
  "https://purl.imsglobal.org/spec/lti/claim/resource_link": z
    .object({
      id: z.string(),
      description: z.string().nullable().optional(),
      title: z.string().optional(),
    })
    .optional(),

  // User Info
  given_name: z.string().optional(),
  family_name: z.string().optional(),
  name: z.string().optional(),
  email: z.string().email().optional(),
  locale: z.string().optional(),
  picture: z.string().url().optional(),

  "https://purl.imsglobal.org/spec/lti/claim/roles": z.array(z.string()),

  "https://purl.imsglobal.org/spec/lti/claim/context": z
    .object({
      id: z.string(),
      type: z.array(z.string()).optional(),
      label: z.string().optional(),
      title: z.string().optional(),
    })
    .optional(),

  "https://purl.imsglobal.org/spec/lti/claim/tool_platform": z
    .object({
      guid: z.string(),
      contact_email: z.string().optional(),
      description: z.string().optional(),
      name: z.string().optional(),
      url: z.string().optional(),
      product_family_code: z.string().optional(),
      version: z.string().optional(),
    })
    .optional(),

  // sub ids of users this current user is a mentor for
  "https://purl.imsglobal.org/spec/lti/claim/role_scope_mentor": z
    .array(z.string())
    .optional(),

  "https://purl.imsglobal.org/spec/lti/claim/launch_presentation": z
    .object({
      document_target: z.string().optional(),
      height: z.number().optional(),
      width: z.number().optional(),
      return_url: z.string().optional(),
      locale: z.string().optional(),
    })
    .optional(),

  "https://purl.imsglobal.org/spec/lti/claim/lis": z
    .object({
      course_offering_sourcedid: z.string().optional().nullable(),
      course_section_sourcedid: z.string().optional().nullable(),

      outcome_service_url: z.string().optional().nullable(),

      person_sourcedid: z.string().optional().nullable(),
      person_name_full: z.string().optional().nullable(),
      person_name_given: z.string().optional().nullable(),
      person_name_family: z.string().optional().nullable(),
      person_contact_email_primary: z.string().optional().nullable(),

      result_sourcedid: z.string().optional().nullable(),
    })
    .optional(),

  // In LTI 1.3, we can no longer rely on the `oauth_consumer_key` to look up
  // an AppKey record, so we rely on it being added as a custom claim
  "https://purl.imsglobal.org/spec/lti/claim/custom": z.object({
    app_key: z.string(),
  }),

  // LTI Advantage claims
  "https://purl.imsglobal.org/spec/lti-ags/claim/endpoint": z
    .object({
      scope: z.array(z.string()),
      // lineitem: when an LTI message is launching a resource associated to
      // one and only one lineitem, the claim must include the endpoint URL
      // for accessing the associated line item; in all other cases, this
      // property must be either blank or not included in the claim.
      lineitem: z.string().url().optional(),
      // lineitems: the endpoint URL for accessing the line item container for
      // the current context. May be omitted if the tool has no permissions to
      // access this endpoint.
      lineItems: z.string().url().optional(),
    })
    .optional(),
  "https://purl.imsglobal.org/spec/lti-nrps/claim/namesroleservice": z
    .object({
      context_memberships_url: z.string().url(),
      service_versions: z.array(z.string()),
    })
    .optional(),
  // Deep Linking 2.0 claims
  // todo: this is required for Deep Linking, but we need to split this schema
  // into a separate one for Deep Linking requests to support that
  "https://purl.imsglobal.org/spec/lti-dl/claim/deep_linking_settings": z
    .object({
      deep_link_return_url: z.string(),
      accept_types: z.array(z.string()),
      accept_presentation_document_targets: z.array(
        z.union([
          z.literal("frame"),
          z.literal("iframe"),
          z.literal("window"),
          z.literal("embed"),
        ]),
      ),
      accept_media_types: z.string().optional(),
      accept_multiple: z.boolean().optional(),
      accept_lineitem: z.boolean().optional(),
      auto_create: z.boolean().optional(),

      title: z.string().optional(),
      text: z.string().optional(),
      data: z.string().optional(),
    })
    .optional(),
});

// be a union of multiple content item types
export const lti1p3ContentItemSchema = z.object({
  type: z.literal("ltiResourceLink"),
  url: z.string().url(),
  title: z.string().optional(),
  text: z.string().optional(),

  icon: z
    .object({
      url: z.string().url(),
      width: z.number().optional(),
      height: z.number().optional(),
    })
    .optional(),
  thumbnail: z
    .object({
      url: z.string().url(),
      width: z.number().optional(),
      height: z.number().optional(),
    })
    .optional(),

  window: z
    .object({
      targetName: z.string().optional(),
      width: z.number().optional(),
      height: z.number().optional(),
      // Comma-separated list of window features as per the [window.open() definition](https://developer.mozilla.org/en-US/docs/Web/API/Window/open)
      windowFeatures: z.string().optional(),
    })
    .optional(),

  iframe: z
    .object({
      width: z.number().optional(),
      height: z.number().optional(),
    })
    .optional(),

  custom: z.record(z.string(), z.string()).optional(),

  // AGS support
  lineItem: z.object({
    scoreMaximum: z.number(),
    label: z.string().optional().nullable(),
    resourceId: z.string().optional().nullable(),
    tag: z.string().optional().nullable(),
    gradesReleased: z.boolean().optional().nullable(),
  }),

  //todo: whatever these are
  // submission:
  // available:
});

// deep linking response schema: LtiDeepLinkingResponse
// this is a combination of these 2 specs:
//  https://www.imsglobal.org/spec/security/v1p0/#tool-originating-messages
//  https://www.imsglobal.org/spec/lti-dl/v2p0#deep-linking-response-message
export const lti1p3DeepLinkingResponseSchema = oidcTokenSchema.extend({
  // iss is our client ID that we were passed in the request
  // aud is the `iss` of the deep linking request message
  "https://purl.imsglobal.org/spec/lti/claim/message_type": z.literal(
    "LtiDeepLinkingResponse",
  ),
  "https://purl.imsglobal.org/spec/lti/claim/version": z.literal("1.3.0"),
  "https://purl.imsglobal.org/spec/lti/claim/deployment_id": z.string(),
  // this is required if they passed us one on the request
  "https://purl.imsglobal.org/spec/lti-dl/claim/data": z.string().optional(),

  "https://purl.imsglobal.org/spec/lti-dl/claim/content_items": z.array(
    lti1p3ContentItemSchema,
  ),
  "https://purl.imsglobal.org/spec/lti-dl/claim/msg": z.string().optional(),
  "https://purl.imsglobal.org/spec/lti-dl/claim/log": z.string().optional(),
  "https://purl.imsglobal.org/spec/lti-dl/claim/errormsg": z
    .string()
    .optional(),
  "https://purl.imsglobal.org/spec/lti-dl/claim/errorlog": z
    .string()
    .optional(),
});
